// BackgroundProvider — store, scheduler, resolver, and hooks.
// Unified layer model: every background has `layers: [...]` (1 for plain image/gradient, 2–6 for a scene).
// Legacy single-image/gradient bgs are normalized to a 3-layer auto-parallax (back/mid/front).

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import seedBackgrounds from './seedBackgrounds';
import { API_URL, refreshAccessToken } from '../../api/api';
import {
  isIdbSrc,
  idbKey,
  getBlob,
  syncToBackend,
  resolveToUrl,
  getCachedUrl,
  getAllKeys,
  deleteBlob,
  mediaSrcToUrl,
} from './mediaStore';

// Only small UI preferences live in localStorage now — the scene library and
// all media are persisted server-side (GET/POST/PUT/DELETE /scenes + /media),
// which permanently fixes the old QuotaExceededError that silently dropped
// new scenes and uploaded images.
const STORAGE_KEY = 'emhub_bg_prefs_v3';
const SCENES_URL = `${API_URL}/api/settings/backgrounds/scenes`;
const SETTINGS_URL = `${API_URL}/api/settings/backgrounds/settings`;
// The signed-in user's own Live pick (background_user_prefs) — { activeId }.
const MY_LIVE_URL = `${API_URL}/api/settings/backgrounds/my`;
const isSeedId = (id) => typeof id === 'string' && id.startsWith('seed-');

// Turn any local/blob/data/idb media reference into a durable server URL.
// Remote URLs and gradient strings pass through untouched.
async function persistSrc(src, onProgress) {
  if (!src || typeof src !== 'string') return src;
  if (/^https?:\/\//i.test(src)) return src;
  if (/^(linear|radial|conic)-gradient/i.test(src)) return src;
  let blob = null;
  let key = null;
  if (isIdbSrc(src)) {
    key = idbKey(src);
    blob = await getBlob(key);
  } else if (src.startsWith('data:')) {
    try {
      blob = await (await fetch(src)).blob();
    } catch {
      /* ignore */
    }
    key = 'bg-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  } else {
    return src;
  }
  if (!blob || !key) return src;
  onProgress?.(0, blob.size);
  const data = await syncToBackend(key, blob, onProgress);
  return data?.url || src;
}

// Replace every media reference inside a scene object with server URLs.
//
// Each distinct source is uploaded ONCE: a static image's src, thumb and its
// single layer are usually the same file, and used to go up three times.
// `onProgress(fraction)` — 0..1 across every file this scene uploads.
async function materializeMedia(obj, onProgress) {
  const uploads = new Map(); // src → Promise<url>
  const bytes = new Map(); // src → { loaded, total }
  const report = () => {
    let loaded = 0;
    let total = 0;
    bytes.forEach((b) => { loaded += b.loaded; total += b.total; });
    if (total > 0) onProgress?.(Math.min(1, loaded / total));
  };
  const persist = (src) => {
    if (!src || typeof src !== 'string') return Promise.resolve(src);
    if (!uploads.has(src)) {
      uploads.set(
        src,
        persistSrc(src, (loaded, total) => {
          bytes.set(src, { loaded, total });
          report();
        })
      );
    }
    return uploads.get(src);
  };
  const out = { ...obj };
  const layerSrcs = Array.isArray(obj.layers) ? obj.layers.map((l) => l.src) : [];
  const [src, thumb, ...layerUrls] = await Promise.all([
    persist(obj.src),
    obj.thumb ? persist(obj.thumb) : null,
    ...layerSrcs.map(persist),
  ]);
  out.src = src;
  out.thumb = obj.thumb ? thumb : src;
  if (Array.isArray(obj.layers)) {
    out.layers = obj.layers.map((l, i) => ({ ...l, src: layerUrls[i] }));
  }
  return out;
}

// fetch with the access token — and, like the axios interceptor in api/api.js,
// one silent refresh + retry on a 401. Access tokens last 15 minutes, so the
// first visit of the day starts with yesterday's expired token: without the
// retry every load here 401'd, the provider gave up with no server scenes and
// the dashboard stayed white until a manual refresh. refreshAccessToken shares
// one in-flight refresh with axios. If it fails the session is really over;
// the 401 is returned as before and the axios path handles the logout.
async function authedFetch(url, opts = {}) {
  const send = () => {
    const token = localStorage.getItem('token');
    const headers = { ...(opts.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(url, { ...opts, headers });
  };
  const res = await send();
  if (res.status !== 401 || !localStorage.getItem('token')) return res;
  try {
    await refreshAccessToken();
  } catch (e) {
    return res;
  }
  return send();
}

// Persist org-wide settings (active default / employee default / parallax).
// Fire-and-forget — the local state already updated optimistically.
function saveSettings(patch) {
  return authedFetch(SETTINGS_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  }).catch((e) => console.warn('[backgrounds] settings save failed', e));
}

// ---------- layer defaults ----------
export const LAYER_DEFAULTS = {
  depth: 60,
  scale: 110,
  rotate: 0,
  blur: 0,
  opacity: 100,
  blend: 'normal',
  visible: true,
  offsetX: 0,
  offsetY: 0,
};

export function makeLayer(src, patch = {}) {
  return {
    id: 'l-' + Math.random().toString(36).slice(2, 9),
    src,
    ...LAYER_DEFAULTS,
    ...patch,
  };
}

// ---------- migration ----------
// Legacy `kind:'auto'` (3-layer parallax fan) → new `kind:'static'`
// (single, non-parallax layer with per-image blur/scale/rotate/offset).
// Layers whose src is a data: URL were written by the old SceneBuilder code
// path that exceeded localStorage quota on save. Those backgrounds never
// round-tripped successfully — drop them on load so the store stays clean.
function isDataUrlSrc(src) {
  return typeof src === 'string' && src.startsWith('data:');
}

function migrate(st) {
  if (!st) return st;
  st.backgrounds = (st.backgrounds || [])
    .map((b) => {
      let next = b;
      // Strip dead data-URL layers from any existing background.
      if (next.layers && next.layers.length) {
        const cleaned = next.layers.filter((l) => !isDataUrlSrc(l.src));
        if (cleaned.length !== next.layers.length) {
          next = { ...next, layers: cleaned };
        }
      }
      if (!next.layers || !next.layers.length) {
        const s = next.settings || {};
        const common = { scale: s.scale || 110, blur: s.blur || 0 };
        // Fall back to top-level src only if it isn't itself a data URL.
        const fallback = isDataUrlSrc(next.src) ? null : next.src;
        next = {
          ...next,
          layers: fallback ? [makeLayer(fallback, { ...common })] : [],
        };
      }
      // Normalize legacy kinds to static, unless it's a scene or video.
      if (!next.kind || next.kind === 'auto' || next.kind === 'image') {
        next = { ...next, kind: 'static' };
      }
      return next;
    })
    // A scene/static bg with no layers left is effectively broken — drop it.
    .filter((b) => b.layers && b.layers.length > 0);
  // Retire old default id if gradient seed was removed.
  if (!st.backgrounds.find((b) => b.id === st.defaultId)) {
    st.defaultId = st.backgrounds[0]?.id || null;
  }
  return st;
}

// Minimal in-page toast for storage errors so quota overflows stop being invisible.
// The server's { error } message from a failed response, else `fallback`.
async function errorText(res, fallback) {
  try {
    const j = await res.clone().json();
    if (j && j.error) return j.error;
  } catch (e) {}
  return `${fallback} (HTTP ${res.status})`;
}

function showBgToast(message) {
  if (typeof document === 'undefined') return;
  try {
    const existing = document.getElementById('emhub-bg-toast');
    if (existing) existing.remove();
    const el = document.createElement('div');
    el.id = 'emhub-bg-toast';
    el.textContent = message;
    el.style.cssText =
      'position:fixed;right:24px;bottom:24px;z-index:99999;' +
      'max-width:360px;padding:12px 16px;border-radius:10px;' +
      'background:#991b1b;color:#fff;' +
      'font:500 13px/1.4 system-ui,sans-serif;' +
      'box-shadow:0 12px 32px rgba(0,0,0,.25);';
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 6000);
  } catch (e) {
    // swallow — toast is best-effort
  }
}

// The built-in library minus the org's hidden ids, normalised the same way
// loadState does. Rebuilt from the constant (never subtracted from whatever is
// in state) so restoring a hidden built-in on one device brings it back on
// every other one, instead of it staying gone until localStorage is cleared.
function seedLibrary(hidden = []) {
  const list = seedBackgrounds
    .filter((b) => !hidden.includes(b.id))
    .map((b) => ({ ...b }));
  return migrate({ backgrounds: list, defaultId: null }).backgrounds;
}

function loadState() {
  // Backgrounds always start from the built-in seed library; server scenes are
  // merged in on mount. Only small prefs come from localStorage.
  const base = {
    backgrounds: seedBackgrounds.map((b) => ({ ...b })),
    // "Set as live" — this user's own pick; overrides schedules + defaults for
    // them only. Loaded per user from the server, never from localStorage.
    myLiveId: null,
    defaultId: 'seed-mountain-dawn',
    employeeDefaultId: null,
    parallaxEnabled: true,
    respectReducedMotion: true,
    role: 'admin',
    hiddenSeedIds: [],
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw) || {};
      if (saved.defaultId !== undefined) base.defaultId = saved.defaultId;
      if (saved.employeeDefaultId !== undefined) base.employeeDefaultId = saved.employeeDefaultId;
      if (saved.parallaxEnabled !== undefined) base.parallaxEnabled = saved.parallaxEnabled;
      if (saved.respectReducedMotion !== undefined) base.respectReducedMotion = saved.respectReducedMotion;
      // `role` is no longer restored: the Admin/Employee preview toggle was
      // removed from Settings, so a stale saved "employee" would lock the page
      // out of admin-only controls.
      if (Array.isArray(saved.hiddenSeedIds)) base.hiddenSeedIds = saved.hiddenSeedIds;
    }
  } catch (e) {}
  // Drop any seeds the user has deleted (persisted in hiddenSeedIds) up front
  // so they don't flash before the server settings load.
  if (base.hiddenSeedIds.length) {
    base.backgrounds = seedLibrary(base.hiddenSeedIds);
  }
  return migrate(base);
}

// ---------- resolver helpers ----------
export function inYearlyRange(now, mmddStart, mmddEnd) {
  if (!mmddStart || !mmddEnd) return false;
  const y = now.getFullYear();
  const [sm, sd] = mmddStart.split('-').map(Number);
  const [em, ed] = mmddEnd.split('-').map(Number);
  let start = new Date(y, sm - 1, sd, 0, 0, 0);
  let end = new Date(y, em - 1, ed, 23, 59, 59);
  if (end < start) {
    if (now >= start) end = new Date(y + 1, em - 1, ed, 23, 59, 59);
    else start = new Date(y - 1, sm - 1, sd, 0, 0, 0);
  }
  return now >= start && now <= end;
}

export function inOnceRange(now, start, end) {
  if (!start || !end) return false;
  return now >= new Date(start + 'T00:00:00') && now <= new Date(end + 'T23:59:59');
}

export function inTimeOfDay(now, tStart, tEnd) {
  if (!tStart || !tEnd) return false;
  const mins = now.getHours() * 60 + now.getMinutes();
  const [sh, sm] = tStart.split(':').map(Number);
  const [eh, em] = tEnd.split(':').map(Number);
  const s = sh * 60 + sm;
  const e = eh * 60 + em;
  return s <= e ? mins >= s && mins <= e : mins >= s || mins <= e;
}

export function isActiveSchedule(b, now) {
  const s = b.schedule || {};
  if (s.kind === 'once') return inOnceRange(now, s.start, s.end);
  if (s.kind === 'yearly') return inYearlyRange(now, s.start, s.end);
  if (s.kind === 'timeOfDay') return inTimeOfDay(now, s.timeStart, s.timeEnd);
  return false;
}

// Admin / HR see the admin default; everyone else the employee default (when
// one is set). Read from the signed-in role, not the old preview toggle.
const viewerIsAdminTier = () => {
  try {
    const r = String(localStorage.getItem('role') || '').toLowerCase();
    return r === 'admin' || r === 'hr';
  } catch (e) {
    return false;
  }
};

// What shows right now, in order:
//   1. live      — the viewer's own "Set as live" pick; no schedule check
//   2. scheduled — highest-priority schedule whose window is open
//   3. default   — employee default for employees (falls back to the admin
//                  default), admin default for Admin / HR
//   4. fallback  — first background
export function resolveActive(st, now) {
  const live = st.myLiveId && st.backgrounds.find((b) => b.id === st.myLiveId);
  if (live) return { bg: live, reason: 'live' };
  const scheduled = st.backgrounds
    .filter((b) => isActiveSchedule(b, now))
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  if (scheduled[0]) return { bg: scheduled[0], reason: 'scheduled' };
  const useEmployee = !viewerIsAdminTier() && st.employeeDefaultId;
  const defId = useEmployee ? st.employeeDefaultId : st.defaultId;
  const def = st.backgrounds.find((b) => b.id === defId) ||
    st.backgrounds.find((b) => b.id === st.defaultId);
  if (def)
    return {
      bg: def,
      reason: def.id === st.employeeDefaultId && useEmployee ? 'employee-default' : 'admin-default',
    };
  return { bg: st.backgrounds[0], reason: 'fallback' };
}

export function upcomingSchedule(st, now, limit = 10) {
  const out = [];
  st.backgrounds.forEach((b) => {
    const s = b.schedule || {};
    if (s.kind === 'once' && s.start) {
      const start = new Date(s.start + 'T00:00:00');
      const end = new Date(s.end + 'T23:59:59');
      if (end >= now) out.push({ bg: b, start, end, kind: 'once' });
    } else if (s.kind === 'yearly' && s.start && s.end) {
      const y = now.getFullYear();
      for (const yr of [y, y + 1]) {
        const [sm, sd] = s.start.split('-').map(Number);
        const [em, ed] = s.end.split('-').map(Number);
        let start = new Date(yr, sm - 1, sd);
        let end = new Date(yr, em - 1, ed, 23, 59, 59);
        if (end < start) end = new Date(yr + 1, em - 1, ed, 23, 59, 59);
        if (end >= now) {
          out.push({ bg: b, start, end, kind: 'yearly' });
          break;
        }
      }
    } else if (s.kind === 'timeOfDay' && s.timeStart && s.timeEnd) {
      const [sh, sm] = s.timeStart.split(':').map(Number);
      const [eh, em] = s.timeEnd.split(':').map(Number);
      const d = new Date(now);
      d.setHours(sh, sm, 0, 0);
      const e = new Date(now);
      e.setHours(eh, em, 0, 0);
      if (e < d) e.setDate(e.getDate() + 1);
      if (e >= now) out.push({ bg: b, start: d, end: e, kind: 'timeOfDay' });
    }
  });
  return out.sort((a, b) => a.start - b.start).slice(0, limit);
}

// ---------- render layers ----------
// - scene: user-authored layer stack (parallax on).
// - static: single image layer, no parallax (depth 0 tells renderer to skip
//   mouse multiplier). User controls blur/scale/rotate/offset per-layer.
// - video: single media layer; renderer uses <video> for mediaType 'video',
//   background-image for 'gif'. No parallax.
export function computeRenderLayers(bg) {
  if (!bg?.layers?.length) return [];
  if (bg.kind === 'scene') return bg.layers.filter((l) => l.visible !== false);
  // static / video / unknown: return the single layer, flagged non-parallax.
  const base = bg.layers[0];
  return [{ ...base, depth: 0 }];
}

export function layerBgValue(src) {
  if (!src) return 'transparent';
  const s = String(src);
  if (s.startsWith('linear-gradient') || s.startsWith('radial-gradient')) return s;
  if (s.startsWith('#') || s.startsWith('rgb') || s.startsWith('hsl')) return s;
  return `url("${s}") center/cover no-repeat`;
}

// ---------- context ----------
const BackgroundContext = createContext(null);

export function BackgroundProvider({ children }) {
  const [state, setState] = useState(loadState);
  // Map of indexeddb key -> resolved blob: URL. Rebuilt when refs change.
  const [mediaUrls, setMediaUrls] = useState({});
  // Have the org's background settings (hidden built-ins, default scene) been
  // fetched yet? Until they have, the local seed library on its own resolves to
  // the wrong picture: a scheduled seed such as 'seed-tod-morning' outranks the
  // default by priority, so the dashboard used to paint an unrelated stock photo
  // for a beat before the real background replaced it. That was most visible
  // right after login, because a 401 anywhere calls localStorage.clear() and
  // wipes the cached prefs (defaultId / hiddenSeedIds) that normally mask it.
  // No token means the login page, where there is nothing to fetch — the seeds
  // are all we will ever have, so treat that as hydrated straight away.
  const [hydrated, setHydrated] = useState(() => {
    try {
      return !localStorage.getItem('token');
    } catch (e) {
      return true;
    }
  });

  // Persist ONLY small prefs — never the (potentially huge) media library.
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          defaultId: state.defaultId,
          employeeDefaultId: state.employeeDefaultId,
          parallaxEnabled: state.parallaxEnabled,
          respectReducedMotion: state.respectReducedMotion,
          role: state.role,
          hiddenSeedIds: state.hiddenSeedIds || [],
        })
      );
    } catch (e) {
      console.error('[backgrounds] prefs persist failed:', e);
    }
  }, [
    state.defaultId,
    state.employeeDefaultId,
    state.parallaxEnabled,
    state.respectReducedMotion,
    state.role,
    state.hiddenSeedIds,
  ]);

  // Load the server-backed scene library and merge it with the built-in seeds.
  // The server is the source of truth for user-created scenes.
  //
  // Runs on mount AND on every auth change. Mount alone is not enough: this
  // provider sits above the router (App.js), so it mounts once — on the login
  // page, before a token exists. Both requests 401 there, leaving the library
  // as the 12 local built-ins with no server scenes, and that stale list used
  // to survive the client-side navigation into the app. A manual refresh then
  // mounted the provider with a token and produced a completely different
  // list (server scenes, built-ins minus the hidden ones) — the "many images
  // on login, one after refresh" bug.
  const loadSeq = useRef(0);
  const loadFromServer = useCallback(async () => {
    const seq = ++loadSeq.current;
    const stale = () => seq !== loadSeq.current;   // a newer load has started
    // Scene library
    try {
      const res = await authedFetch(SCENES_URL);
      if (res.ok) {
        const scenes = await res.json();
        if (!stale() && Array.isArray(scenes)) {
          setState((st) => {
            const seeds = (st.backgrounds || []).filter((b) => isSeedId(b.id));
            // Non-seed entries come wholly from this response, so scenes left
            // over from a previous session/user don't linger after re-login.
            return { ...st, backgrounds: [...seeds, ...scenes] };
          });
        }
      }
    } catch (e) {
      console.warn('[backgrounds] could not load scenes from server', e);
    }
    // Shared org-wide settings (default / employee default / parallax)
    try {
      const res = await authedFetch(SETTINGS_URL);
      if (res.ok) {
        const s = await res.json();
        if (!stale() && s) {
          setState((st) => {
            const hidden = Array.isArray(s.hiddenSeedIds)
              ? s.hiddenSeedIds
              : st.hiddenSeedIds || [];
            // Keep the seed objects already in state (a seed tweaked in the
            // edit drawer is local-only) and take fresh copies from the
            // constant for any that were hidden and have since come back.
            const inState = new Map(
              st.backgrounds.filter((b) => isSeedId(b.id)).map((b) => [b.id, b])
            );
            const seeds = seedLibrary(hidden).map((b) => inState.get(b.id) || b);
            return {
              ...st,
              defaultId: s.defaultId ?? st.defaultId,
              employeeDefaultId: s.employeeDefaultId ?? st.employeeDefaultId,
              parallaxEnabled:
                s.parallaxEnabled == null ? st.parallaxEnabled : s.parallaxEnabled,
              respectReducedMotion:
                s.respectReducedMotion == null
                  ? st.respectReducedMotion
                  : s.respectReducedMotion,
              hiddenSeedIds: hidden,
              // Rebuild the built-in half from code against the server's hidden
              // list — this both drops seeds deleted on another device and
              // brings back ones that were un-hidden there.
              backgrounds: [
                ...seeds,
                ...st.backgrounds.filter((b) => !isSeedId(b.id)),
              ],
            };
          });
        }
      }
    } catch (e) {
      console.warn('[backgrounds] could not load settings from server', e);
    }
    // This user's own Live pick. Reset first, so a previous user's pick on a
    // shared browser can't survive a re-login.
    if (!stale()) setState((st) => ({ ...st, myLiveId: null }));
    try {
      const res = await authedFetch(MY_LIVE_URL);
      if (res.ok) {
        const my = await res.json();
        if (!stale()) setState((st) => ({ ...st, myLiveId: my?.activeId || null }));
      }
    } catch (e) {
      console.warn('[backgrounds] could not load your live background', e);
    }
    // Success or failure, we've done all we can — let the background render.
    if (!stale()) setHydrated(true);
  }, []);

  useEffect(() => {
    loadFromServer();
    // Login.js dispatches 'emhub:login' right after writing the token; the
    // 'storage' event covers a login/logout that happened in another tab.
    // (Logout itself hard-navigates to /login, which remounts everything.)
    // An auth change invalidates the settings resolved for the old session, so
    // hide the background again until the new ones land rather than showing the
    // previous user's (or the seed library's) pick in the meantime.
    const reload = () => {
      setHydrated(false);
      loadFromServer();
    };
    const onLogin = () => reload();
    const onStorage = (e) => {
      if (!e || !e.key || e.key === 'token') reload();
    };
    window.addEventListener('emhub:login', onLogin);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('emhub:login', onLogin);
      window.removeEventListener('storage', onStorage);
    };
  }, [loadFromServer]);

  // Whenever the set of indexeddb-backed layers changes, resolve blob URLs.
  useEffect(() => {
    let cancelled = false;
    const keys = new Set();
    state.backgrounds.forEach((b) =>
      (b.layers || []).forEach((l) => {
        if (isIdbSrc(l.src)) keys.add(idbKey(l.src));
      })
    );
    (async () => {
      const next = {};
      await Promise.all(
        Array.from(keys).map(async (k) => {
          const cached = getCachedUrl(k);
          if (cached) {
            next[k] = cached;
            return;
          }
          // Backgrounds are decorative — a media lookup that fails (IDB
          // closing during navigation, quota, private browsing) must degrade
          // to "no background", never reject and surface as an uncaught
          // runtime error over the whole app.
          try {
            const url = await resolveToUrl(k);
            if (url) next[k] = url;
          } catch (e) {
            console.warn('[backgrounds] could not resolve media', k, e?.message);
          }
        })
      );
      if (!cancelled) setMediaUrls(next);
    })();
    // Garbage-collect orphan IDB blobs from the LOCAL cache only. We must NOT
    // delete the backend /media copies here: scenes now reference media by
    // server URL (not by `indexeddb:` key), so every idb key looks "orphaned"
    // even while a live scene still points at its uploaded file. Server-side
    // media cleanup happens on explicit scene delete instead.
    (async () => {
      try {
        const allKeys = await getAllKeys();
        const orphans = allKeys.filter((k) => !keys.has(k));
        await Promise.all(orphans.map((k) => deleteBlob(k).catch(() => {})));
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [state.backgrounds]);

  // re-tick every 30s so scheduled/time-of-day resolves update
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setTick(Date.now()), 30000);
    return () => clearInterval(i);
  }, []);

  // Uploads in progress, for the library to show as placeholder cards:
  // [{ id, name, phase: 'preparing' | 'uploading' | 'saving', pct }].
  // An entry lives from the moment a file is picked (startUpload) until the
  // background is saved or fails (addBackground ends it either way).
  const [uploads, setUploads] = useState([]);
  const patchUpload = useCallback((id, p) => {
    setUploads((list) => list.map((u) => (u.id === id ? { ...u, ...p } : u)));
  }, []);
  const startUpload = useCallback((name) => {
    const id = 'up-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    setUploads((list) => [
      ...list,
      { id, name: String(name || 'Background').replace(/\.[^.]+$/, ''), phase: 'preparing', pct: 0 },
    ]);
    return id;
  }, []);
  const endUpload = useCallback((id) => {
    if (id) setUploads((list) => list.filter((u) => u.id !== id));
  }, []);

  // Create → upload media to the server, POST the scene, append what comes back
  // (which carries durable server URLs). Returns the saved scene (or null).
  // `uploadId` continues a placeholder started with startUpload (e.g. while
  // the image was being compressed); without one, a new placeholder is made.
  const addBackground = useCallback(async (b, { uploadId } = {}) => {
    const uid = uploadId || startUpload(b?.name);
    try {
      patchUpload(uid, { phase: 'uploading', pct: 0 });
      const mat = await materializeMedia(b, (f) => patchUpload(uid, { phase: 'uploading', pct: f }));
      patchUpload(uid, { phase: 'saving', pct: 1 });
      const payload = {
        name: mat.name,
        type: mat.type,
        kind: mat.kind,
        mediaType: mat.mediaType,
        tags: mat.tags,
        priority: mat.priority,
        src: mat.src,
        thumb: mat.thumb,
        layers: mat.layers,
        settings: mat.settings,
        schedule: mat.schedule,
      };
      const res = await authedFetch(SCENES_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const saved = await res.json();
      setState((st) => ({ ...st, backgrounds: [...st.backgrounds, saved] }));
      return saved;
    } catch (e) {
      console.error('[backgrounds] create failed:', e);
      showBgToast(`Couldn't save background: ${e?.message || 'unknown error'}`);
      return null;
    } finally {
      endUpload(uid);
    }
  }, [startUpload, patchUpload, endUpload]);

  // Update → optimistic local merge, then PUT (server scenes only; seeds are
  // built-in and stay local). Re-materialises layer media if layers changed.
  const updateBackground = useCallback(async (id, patch) => {
    setState((st) => ({
      ...st,
      backgrounds: st.backgrounds.map((b) =>
        b.id === id
          ? {
              ...b,
              ...patch,
              settings: { ...(b.settings || {}), ...(patch.settings || {}) },
              schedule: { ...(b.schedule || {}), ...(patch.schedule || {}) },
              layers: patch.layers ? patch.layers : b.layers,
            }
          : b
      ),
    }));
    if (isSeedId(id)) return;
    try {
      const mat = patch.layers || patch.src || patch.thumb
        ? await materializeMedia(patch)
        : patch;
      const body = {};
      ['name', 'settings', 'schedule', 'layers', 'priority', 'tags', 'src', 'thumb', 'type', 'kind', 'mediaType', 'shared'].forEach(
        (k) => {
          if (k in mat) body[k] = mat[k];
        }
      );
      const res = await authedFetch(`${SCENES_URL}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const saved = await res.json();
        setState((st) => ({
          ...st,
          backgrounds: st.backgrounds.map((b) => (b.id === id ? saved : b)),
        }));
      } else {
        // Refused (not the uploader) — undo the optimistic merge.
        showBgToast(await errorText(res, "Couldn't update background"));
        loadFromServer();
      }
    } catch (e) {
      console.error('[backgrounds] update failed:', e);
      showBgToast(`Couldn't update background: ${e?.message || 'unknown error'}`);
    }
  }, [loadFromServer]);

  const deleteBackground = useCallback(async (id) => {
    let hiddenAfter = null;
    setState((st) => {
      const next = {
        ...st,
        backgrounds: st.backgrounds.filter((b) => b.id !== id),
      };
      if (next.myLiveId === id) next.myLiveId = null;
      if (next.defaultId === id) next.defaultId = null;
      if (next.employeeDefaultId === id) next.employeeDefaultId = null;
      // Built-in seeds can't be deleted server-side (they reload from code), so
      // record the id in hiddenSeedIds — persisted to the shared settings so it
      // stays gone across reloads/devices.
      if (isSeedId(id)) {
        hiddenAfter = Array.from(new Set([...(st.hiddenSeedIds || []), id]));
        next.hiddenSeedIds = hiddenAfter;
      }
      return next;
    });
    if (isSeedId(id)) {
      if (hiddenAfter) saveSettings({ hiddenSeedIds: hiddenAfter });
      return;
    }
    // Removed optimistically above; if the server refuses (not the uploader)
    // or the request fails, reload so the card comes back.
    try {
      const res = await authedFetch(`${SCENES_URL}/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        showBgToast(await errorText(res, "Couldn't delete background"));
        loadFromServer();
      }
    } catch (e) {
      console.warn('[backgrounds] delete failed:', e);
      showBgToast("Couldn't delete background");
      loadFromServer();
    }
  }, [loadFromServer]);
  // Optimistic, but rolled back when the server refuses — e.g. a private upload
  // can't be the org default until it is shared with everyone.
  const saveDefaultKey = useCallback(async (key, id) => {
    let prev;
    setState((st) => {
      prev = st[key];
      return { ...st, [key]: id };
    });
    const res = await saveSettings({ [key]: id });
    if (res && !res.ok) {
      setState((st) => ({ ...st, [key]: prev ?? null }));
      showBgToast(await errorText(res, "Couldn't change the default"));
    }
  }, []);
  // "Set as live" (id) / stop (null) — for THIS user only, Admin included:
  // overrides schedules and defaults on their own screen, nobody else's.
  // Optimistic; rolled back if the server refuses (e.g. not visible to them).
  const setLive = useCallback(async (id) => {
    let prev;
    setState((st) => {
      prev = st.myLiveId;
      return { ...st, myLiveId: id || null };
    });
    try {
      const res = await authedFetch(MY_LIVE_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activeId: id || null }),
      });
      if (!res.ok) {
        setState((st) => ({ ...st, myLiveId: prev ?? null }));
        showBgToast(await errorText(res, "Couldn't set your live background"));
      }
    } catch (e) {
      setState((st) => ({ ...st, myLiveId: prev ?? null }));
      showBgToast("Couldn't set your live background");
    }
  }, []);
  const setDefault = useCallback((id) => saveDefaultKey('defaultId', id), [saveDefaultKey]);
  const setEmployeeDefault = useCallback(
    (id) => saveDefaultKey('employeeDefaultId', id),
    [saveDefaultKey]
  );
  const setParallaxEnabled = useCallback((v) => {
    setState((st) => ({ ...st, parallaxEnabled: v }));
    saveSettings({ parallaxEnabled: !!v });
  }, []);
  const setRole = useCallback(
    (role) => setState((st) => ({ ...st, role })),
    []
  );
  // Reset = back to the full built-in library. hiddenSeedIds has to be cleared
  // ON THE SERVER too, not just locally: it's an org-wide row, so a local-only
  // reset put the built-ins back on screen and then lost them again on the next
  // refresh, which is exactly the inconsistency this page suffered from.
  const resetAll = useCallback(async () => {
    localStorage.removeItem(STORAGE_KEY);
    setState(loadState());
    await saveSettings({ hiddenSeedIds: [] });
    loadFromServer();
  }, [loadFromServer]);

  const active = useMemo(
    () =>
      hydrated
        ? resolveActive(state, new Date(tick))
        : { bg: null, reason: 'loading' },
    [hydrated, state, tick]
  );

  const resolveSrc = useCallback(
    (src) => {
      if (!src) return src;
      if (isIdbSrc(src)) return mediaUrls[idbKey(src)] || null;
      // Server-stored media is a root-relative path; join it to the public
      // uploads base (and repair the legacy prefix-less absolute URLs).
      return mediaSrcToUrl(src);
    },
    [mediaUrls]
  );

  const value = useMemo(
    () => ({
      state,
      active,
      hydrated,
      mediaUrls,
      resolveSrc,
      addBackground,
      updateBackground,
      deleteBackground,
      setLive,
      setDefault,
      setEmployeeDefault,
      setParallaxEnabled,
      setRole,
      resetAll,
      uploads,
      startUpload,
      endUpload,
    }),
    [
      uploads,
      startUpload,
      endUpload,
      setLive,
      state,
      active,
      hydrated,
      mediaUrls,
      resolveSrc,
      addBackground,
      updateBackground,
      deleteBackground,
      setDefault,
      setEmployeeDefault,
      setParallaxEnabled,
      setRole,
      resetAll,
    ]
  );

  return (
    <BackgroundContext.Provider value={value}>
      {children}
    </BackgroundContext.Provider>
  );
}

export function useBackgroundStore() {
  const ctx = useContext(BackgroundContext);
  if (!ctx) throw new Error('useBackgroundStore must be used inside <BackgroundProvider>');
  return ctx;
}

export function useActiveBackground() {
  return useBackgroundStore().active;
}
